import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import { metricLabel } from "../../utils/metricOptions";
import { useReportComparison } from "../../hooks/useReportData";
import type { BreakdownBlock } from "../../api/types";
import type { ComparisonGroup } from "../../../../../../shared/api/types";

interface BreakdownViewProps {
  block: BreakdownBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

const fmt = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "–";
  return `${Math.round(value * 10) / 10}`;
};

/**
 * Breakdown table: one column per metric, one row per comparison group
 * (gender / age / department / shift). Values are shown neutrally as the
 * comparison endpoint carries no per-cell RAG data.
 */
export const BreakdownView: React.FC<BreakdownViewProps> = ({
  block,
  clientId,
  branding,
  fill,
}) => {
  const theme = useTheme();
  const accent = branding?.primaryColor ?? theme.palette.primary.main;
  // In a tight column context cap to 3 metrics to avoid horizontal overflow.
  const visibleMetrics = fill === false ? block.metrics.slice(0, 3) : block.metrics;
  const query = useReportComparison(
    block.dimension,
    block.metrics,
    block.filters,
    clientId,
  );

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (query.isError) {
    return <Alert severity="error">Couldn't load breakdown data.</Alert>;
  }

  const groups: ComparisonGroup[] = query.data?.data?.groups ?? [];
  if (groups.length === 0) {
    return <Alert severity="info">No breakdown data for this period.</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      {block.title && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {block.title}
        </Typography>
      )}

      <Table size="small" component={Paper} sx={{ overflow: "hidden" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Group</TableCell>
            {visibleMetrics.map((m) => (
              <TableCell key={m} align="center" sx={{ fontWeight: 700 }}>
                {metricLabel(m)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <TableRow key={group.group_key}>
              <TableCell sx={{ fontWeight: 600 }}>{group.group_label}</TableCell>
              {visibleMetrics.map((m) => (
                <TableCell key={m} align="center">
                  {fmt(group.values?.[m])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {block.keyMessage && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            borderLeft: `4px solid ${accent}`,
            bgcolor: alpha(accent, 0.08),
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: accent, fontWeight: 700, letterSpacing: "0.06em" }}
          >
            Key Message
          </Typography>
          <Typography variant="body1">{block.keyMessage}</Typography>
        </Box>
      )}
    </Box>
  );
};
